import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "dropdown", "mentions"]

  connect() {
    this.mentionedUsers = new Set()
    this.boundClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    this.syncExistingMentions()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  onInput() {
    const textarea = this.textareaTarget
    const cursorPos = textarea.selectionStart
    const textBeforeCursor = textarea.value.substring(0, cursorPos)
    const mentionMatch = textBeforeCursor.match(/@([\p{L}\p{N}]*)$/u)

    if (mentionMatch) {
      this.searchUsers(mentionMatch[1])
    } else {
      this.hideDropdown()
    }
  }

  onKeydown(event) {
    if (!this.dropdownVisible) return

    if (event.key === "Escape") {
      event.preventDefault()
      this.hideDropdown()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveSelection(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveSelection(-1)
    } else if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault()
      this.selectCurrent()
    }
  }

  async searchUsers(query) {
    const url = "/search/users?q=" + encodeURIComponent(query)
    const response = await fetch(url)
    const users = await response.json()

    if (users.length === 0) {
      this.hideDropdown()
      return
    }

    this.currentResults = users
    this.selectedIndex = 0
    this.renderDropdown(users)
  }

  renderDropdown(users) {
    const dropdown = this.dropdownTarget
    dropdown.innerHTML = users.map((user, index) => {
      const selected = index === this.selectedIndex ? "bg-gold/10" : ""
      const initials = user.label.split(" ").map(n => n[0]).join("")
      const mentioned = this.mentionedUsers.has(user.id)
        ? ' <span class="text-[9px] text-success ml-1">mentionné</span>'
        : ""
      return '<button type="button" ' +
        'class="w-full text-left px-3 py-2 hover:bg-gold/10 transition-colors flex items-center justify-between gap-2 ' + selected + '" ' +
        'data-action="click->mention#selectUser" ' +
        'data-user-id="' + user.id + '" ' +
        'data-user-name="' + this.escapeHtml(user.label) + '">' +
        '<div class="flex items-center gap-2">' +
          '<div class="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center text-[10px] font-bold text-gold">' +
            this.escapeHtml(initials) +
          '</div>' +
          '<span class="text-sm font-medium text-text-primary">' + this.escapeHtml(user.label) + mentioned + '</span>' +
        '</div>' +
        '<span class="text-[10px] text-text-muted">' + this.escapeHtml(user.role) + '</span>' +
      '</button>'
    }).join("")

    dropdown.classList.remove("hidden")
    this.dropdownVisible = true
  }

  selectUser(event) {
    event.preventDefault()
    event.stopPropagation()
    const userId = parseInt(event.currentTarget.dataset.userId)
    const userName = event.currentTarget.dataset.userName
    this.insertMention(userId, userName)
  }

  selectCurrent() {
    if (!this.currentResults || this.selectedIndex < 0) return
    const user = this.currentResults[this.selectedIndex]
    this.insertMention(user.id, user.label)
  }

  insertMention(userId, userName) {
    const textarea = this.textareaTarget
    const cursorPos = textarea.selectionStart
    const textBeforeCursor = textarea.value.substring(0, cursorPos)
    const textAfterCursor = textarea.value.substring(cursorPos)
    const mentionStart = textBeforeCursor.lastIndexOf("@")

    if (mentionStart === -1) return

    const newText = textBeforeCursor.substring(0, mentionStart) + "@" + userName + " " + textAfterCursor
    textarea.value = newText
    textarea.selectionStart = textarea.selectionEnd = mentionStart + userName.length + 2

    this.mentionedUsers.add(userId)
    this.updateHiddenField()
    this.hideDropdown()
    textarea.focus()
  }

  moveSelection(direction) {
    if (!this.currentResults) return
    this.selectedIndex = Math.max(0, Math.min(this.currentResults.length - 1, this.selectedIndex + direction))
    this.renderDropdown(this.currentResults)
  }

  hideDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
    this.dropdownVisible = false
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  updateHiddenField() {
    if (this.hasMentionsTarget) {
      this.mentionsTarget.value = Array.from(this.mentionedUsers).join(",")
    }
  }

  syncExistingMentions() {
    if (this.hasMentionsTarget && this.mentionsTarget.value) {
      this.mentionsTarget.value.split(",").filter(Boolean).forEach(id => {
        this.mentionedUsers.add(parseInt(id))
      })
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text || ""
    return div.innerHTML
  }
}
